import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import UserNav from '../UserNav/UserNav';
import ScreenHostOnboardingProvider from '../../ScreenHost/ScreenHostOnboarding/ScreenHostOnboardingProvider';
import { getUserAccessToken, getUserSession, persistUserSession } from '../../../services/userSession';
import { getScreenHostAccount } from '../../../services/getScreenHostAccount';
import { loadAndPersistScreenHostTeamContext, resolveScreenHostContext } from '../../../services/resolveScreenHostContext';
import { hostRoleCanManageTeam } from '../../../services/screenHostTeam';
import { useSessionRefresh } from '../../../hooks/useSessionRefresh';
import '../UserDashboard/UserDashboard.css';

export default function ScreenHostShell({ children }) {
  useSessionRefresh();
  const location = useLocation();
  const navigate = useNavigate();
  const [redirectingToAgreement, setRedirectingToAgreement] = useState(false);
  const [bootstrapped, setBootstrapped] = useState(false);
  const state = location.state || (() => {
    try {
      const savedState = sessionStorage.getItem('userDashboardState');
      return savedState ? JSON.parse(savedState) : getUserSession();
    } catch (e) {
      console.error(e);
      return getUserSession();
    }
  })();
  const token = getUserAccessToken() || state?.token;
  const { userId: resolvedUserId, role } = resolveScreenHostContext(state || {}, token);
  const userId = resolvedUserId || localStorage.getItem('userId');

  useEffect(() => {
    if (location.state) {
      sessionStorage.setItem('userDashboardState', JSON.stringify(location.state));
      persistUserSession(location.state);
    }
  }, [location.state]);

  useEffect(() => {
    if (!token) {
      navigate('/');
      return undefined;
    }

    let cancelled = false;

    (async () => {
      try {
        const [accountResponse] = await Promise.all([
          getScreenHostAccount(token),
          loadAndPersistScreenHostTeamContext(token, persistUserSession).catch(() => null),
        ]);

        if (cancelled) {
          return;
        }

        const data = accountResponse.data;
        if (!data?.userId) {
          navigate('/');
          return;
        }

        const teamContext = resolveScreenHostContext(getUserSession() || {}, token);
        const nextState = {
          userId: teamContext.ownerUserId || data.userId,
          ownerUserId: teamContext.ownerUserId || data.userId,
          actorUserId: teamContext.actorUserId || data.userId,
          hostRole: teamContext.role || 'owner',
          teamMemberOnly: Boolean(data.teamMemberOnly),
          phoneNumber: data.phoneNumber,
          token,
        };
        sessionStorage.setItem('userDashboardState', JSON.stringify(nextState));
        persistUserSession(nextState);

        if (!data.accountActivated) {
          setRedirectingToAgreement(true);
          navigate('/screen-host/accept', { replace: true, state: nextState });
          return;
        }

        if (location.pathname === '/user/team' && !hostRoleCanManageTeam(nextState.hostRole)) {
          navigate('/user/screens', { replace: true });
          return;
        }

        setBootstrapped(true);
      } catch {
        if (cancelled) {
          return;
        }
        if (!userId) {
          navigate('/');
          return;
        }
        setBootstrapped(true);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [navigate, token, userId, location.pathname]);

  if (redirectingToAgreement || !bootstrapped) {
    return null;
  }

  return (
    <ScreenHostOnboardingProvider skipOnboarding={resolveScreenHostContext(getUserSession() || {}, token).teamMemberOnly}>
      <div>
        <UserNav hostRole={role} />
        {children}
      </div>
    </ScreenHostOnboardingProvider>
  );
}
